import { MessageDescriptor, defineMessages } from 'react-intl'
import { CantonId, unCantonId } from '../../domain/index'

type DescriptorVariant = 'full' | 'short'

const fallbackDescriptor = {
  id: 'cantons.unknown',
  defaultMessage: '[Unbekannt]',
}

export const cantonDescriptor = (id: CantonId, variant: DescriptorVariant): MessageDescriptor => {
  switch (variant) {
    case 'full':
      return cantonDescriptorsFull[unCantonId(id)] || fallbackDescriptor
    case 'short':
      return cantonDescriptorsShort[unCantonId(id)] || fallbackDescriptor
  }
}

export interface CantonDescriptorsLookupTable {
  // Index signature so that we can access keys using [] notation.
  [id: string]: undefined | MessageDescriptor
}

export const cantonDescriptorsFull: CantonDescriptorsLookupTable = defineMessages({
  AG: {
    id: 'cantons.full.ag',
    defaultMessage: 'Aargau',
  },
  AI: {
    id: 'cantons.full.ai',
    defaultMessage: 'Appenzell Innerrhoden',
  },
  AR: {
    id: 'cantons.full.ar',
    defaultMessage: 'Appenzell Ausserrhoden',
  },
  BE: {
    id: 'cantons.full.be',
    defaultMessage: 'Bern',
  },
  BL: {
    id: 'cantons.full.bl',
    defaultMessage: 'Basel-Landschaft',
  },
  BS: {
    id: 'cantons.full.bs',
    defaultMessage: 'Basel-Stadt',
  },
  FR: {
    id: 'cantons.full.fr',
    defaultMessage: 'Freiburg',
  },
  GE: {
    id: 'cantons.full.ge',
    defaultMessage: 'Genf',
  },
  GL: {
    id: 'cantons.full.gl',
    defaultMessage: 'Glarus',
  },
  GR: {
    id: 'cantons.full.gr',
    defaultMessage: 'Graubünden',
  },
  JU: {
    id: 'cantons.full.ju',
    defaultMessage: 'Jura',
  },
  LU: {
    id: 'cantons.full.lu',
    defaultMessage: 'Luzern',
  },
  NE: {
    id: 'cantons.full.ne',
    defaultMessage: 'Neuenburg',
  },
  NW: {
    id: 'cantons.full.nw',
    defaultMessage: 'Nidwalden',
  },
  OW: {
    id: 'cantons.full.ow',
    defaultMessage: 'Obwalden',
  },
  SG: {
    id: 'cantons.full.sg',
    defaultMessage: 'St.\u202FGallen', // narrow no-break space
  },
  SH: {
    id: 'cantons.full.sh',
    defaultMessage: 'Schaffhausen',
  },
  SO: {
    id: 'cantons.full.so',
    defaultMessage: 'Solothurn',
  },
  SZ: {
    id: 'cantons.full.sz',
    defaultMessage: 'Schwyz',
  },
  TG: {
    id: 'cantons.full.tg',
    defaultMessage: 'Thurgau',
  },
  TI: {
    id: 'cantons.full.ti',
    defaultMessage: 'Tessin',
  },
  UR: {
    id: 'cantons.full.ur',
    defaultMessage: 'Uri',
  },
  VD: {
    id: 'cantons.full.vd',
    defaultMessage: 'Waadt',
  },
  VS: {
    id: 'cantons.full.vs',
    defaultMessage: 'Wallis',
  },
  ZG: {
    id: 'cantons.full.zg',
    defaultMessage: 'Zug',
  },
  ZH: {
    id: 'cantons.full.zh',
    defaultMessage: 'Zürich',
  },
})

export const cantonDescriptorsShort: CantonDescriptorsLookupTable = defineMessages({
  AG: {
    id: 'cantons.short.ag',
    defaultMessage: 'Aargau',
  },
  AI: {
    id: 'cantons.short.ai',
    defaultMessage: 'Appenzell I.\u202FRh.', // narrow no-break space
  },
  AR: {
    id: 'cantons.short.ar',
    defaultMessage: 'Appenzell A.\u202FRh.', // narrow no-break space
  },
  BE: {
    id: 'cantons.short.be',
    defaultMessage: 'Bern',
  },
  BL: {
    id: 'cantons.short.bl',
    defaultMessage: 'Basel-Landsch.',
  },
  BS: {
    id: 'cantons.short.bs',
    defaultMessage: 'Basel-Stadt',
  },
  FR: {
    id: 'cantons.short.fr',
    defaultMessage: 'Freiburg',
  },
  GE: {
    id: 'cantons.short.ge',
    defaultMessage: 'Genf',
  },
  GL: {
    id: 'cantons.short.gl',
    defaultMessage: 'Glarus',
  },
  GR: {
    id: 'cantons.short.gr',
    defaultMessage: 'Graubünden',
  },
  JU: {
    id: 'cantons.short.ju',
    defaultMessage: 'Jura',
  },
  LU: {
    id: 'cantons.short.lu',
    defaultMessage: 'Luzern',
  },
  NE: {
    id: 'cantons.short.ne',
    defaultMessage: 'Neuenburg',
  },
  NW: {
    id: 'cantons.short.nw',
    defaultMessage: 'Nidwalden',
  },
  OW: {
    id: 'cantons.short.ow',
    defaultMessage: 'Obwalden',
  },
  SG: {
    id: 'cantons.short.sg',
    defaultMessage: 'St.\u202FGallen', // narrow no-break space
  },
  SH: {
    id: 'cantons.short.sh',
    defaultMessage: 'Schaffhausen',
  },
  SO: {
    id: 'cantons.short.so',
    defaultMessage: 'Solothurn',
  },
  SZ: {
    id: 'cantons.short.sz',
    defaultMessage: 'Schwyz',
  },
  TG: {
    id: 'cantons.short.tg',
    defaultMessage: 'Thurgau',
  },
  TI: {
    id: 'cantons.short.ti',
    defaultMessage: 'Tessin',
  },
  UR: {
    id: 'cantons.short.ur',
    defaultMessage: 'Uri',
  },
  VD: {
    id: 'cantons.short.vd',
    defaultMessage: 'Waadt',
  },
  VS: {
    id: 'cantons.short.vs',
    defaultMessage: 'Wallis',
  },
  ZG: {
    id: 'cantons.short.zg',
    defaultMessage: 'Zug',
  },
  ZH: {
    id: 'cantons.short.zh',
    defaultMessage: 'Zürich',
  },
})
